import { TextVerticalAlign, TextWritingType, type BaseTextInfo, type TextContentPadding } from '@/version-types';

/**
 * 获取文本图层垂直布局样式
 * @param verticalAlign - 文本垂直对齐方式
 * @param writingMode - 文本方向
 * @returns - 文本图层样式
 */
export const getVerticalLayoutStyles = (verticalAlign: TextVerticalAlign, writingMode: TextWritingType) => {
    if (!verticalAlign) return {};

    const styles: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
    };

    const justifyDict = {
        top: 'flex-start',
        center: 'center',
        bottom: 'flex-end',
    };
    const justifyContent = (() => {
        if (writingMode === TextWritingType.HORIZONTAL_TB) return justifyDict[verticalAlign];

        if (verticalAlign === 'top') return justifyDict.bottom;
        if (verticalAlign === 'bottom') return justifyDict.top;
        return justifyDict[verticalAlign];
    })();

    styles.justifyContent = justifyContent;

    return styles;
};

/**
 * 获取文本样式
 * @param layer - 文本图层
 * @returns - 文本样式
 */
export const getBaseTextStyle = (data: BaseTextInfo, options: { preservePadding?: TextContentPadding } = {}) => {
    const {
        color,
        fontFamily,
        fontSize,
        lineSpacing,
        letterSpacing,
        bold,
        italic,
        lineThrough,
        underline,
        textAlign,
        writingMode,
        opacity,
    } = data;

    let styles: React.CSSProperties = {
        // --- layout properties ---
        width: '100%',
        height: '100%',
        margin: 0,

        // --- position properties ---
        position: 'absolute',
        top: 0,
        left: 0,

        // --- font relative properties ---
        fontSize: `${fontSize}px`,
        fontFamily,
        letterSpacing: `${letterSpacing}px`,
        fontWeight: bold ? 'bold' : 'normal',
        fontStyle: italic ? 'italic' : 'normal',
        writingMode,
        lineHeight: `${lineSpacing < 0.01 ? 0.01 : lineSpacing}`,
        textDecoration: `${lineThrough ? 'line-through' : ''} ${underline ? 'underline' : ''}`,
        textAlign,
        whiteSpace: 'break-spaces',
        wordBreak: 'break-word',
        lineBreak: 'auto',
        WebkitTextFillColor: '',
        WebkitTextSizeAdjust: 'none',
        color,

        // --- background properties ---
        backgroundImage: '',
        WebkitBackgroundClip: '',
        backgroundClip: '',

        // --- whole element properties ---
        opacity,
        outline: 'none',
    };

    if (options.preservePadding) {
        const { top, right, bottom, left } = options.preservePadding;
        styles = {
            ...styles,
            paddingTop: top,
            paddingRight: right,
            paddingBottom: bottom,
            paddingLeft: left,
        };
    }

    if (data.verticalAlign) {
        const verticalLayoutStyles = getVerticalLayoutStyles(data.verticalAlign, data.writingMode);
        styles = { ...styles, ...verticalLayoutStyles };
    }

    return styles;
};
